/**
 * Tracker de combate da sala (tabela embutida; ver ./model.ts). O mestre monta a
 * ordem, rola iniciativas, passa os turnos (avisando no chat) e controla efeitos;
 * os jogadores acompanham, sem ver os atores privados nem o que é protegido. O ator
 * ligado a um token invisível (BRAZ-24) só aparece para quem vê o token, com o selo.
 */
import { useCallback, useEffect, useState } from 'react';
import { openDoc, releaseDoc } from '../../plugins/ndbClient.js';
import { useT } from '../../i18n/index.js';
import type { ReplicaNodeDatabase } from '../../ndb.js';
import { rolar } from '../../shared.js';
import { rollProblem } from '../../dice/rollCheck.js';
import type { Op } from '../../ndb.js';
import { useApp } from '../../store.js';
import { Icon } from '../../ui/icons.js';
import { ConditionIcon } from '../../scene/conditions.js';
import { askConfirm, askText } from '../../ui/prompt.js';
import { toast } from '../../ui/Toaster.js';
import { addActorOps, addEffectOps, linkActorOps, nextTurn, prevTurn, readTracker, resetOps, setOps, sortActors, turnLeaves, visibilityOps, type Actor, type Side, type Tracker, type TurnChange, type Visibility } from './model.js';
import './combat.css';

const SIDE_NEXT: Record<Side, Side> = { '': 'friend', friend: 'neutral', neutral: 'foe', foe: '' };
const VIS_NEXT: Record<Visibility, Visibility> = { public: 'protected', protected: 'private', private: 'public' };

/** RNG local (rolagem de atores privados, que não vai ao chat). */
const localRng = (max: number) => {
  const a = new Uint32Array(1);
  crypto.getRandomValues(a);
  return a[0]! % max;
};

export function CombatTracker({ itemId }: { itemId: string }) {
  const t = useT();
  const [db, setDb] = useState<ReplicaNodeDatabase | null>(null);
  const [, setVersion] = useState(0);

  useEffect(() => {
    let alive = true;
    let off: (() => void) | undefined;
    void openDoc(itemId).then((opened) => {
      if (!alive) return;
      setDb(opened);
      off = opened.subscribe(() => setVersion((v) => v + 1));
    });
    return () => {
      alive = false;
      off?.();
      releaseDoc(itemId);
    };
  }, [itemId]);

  if (!db) {
    return (
      <div className="rd-module__placeholder">
        <span className="rd-spinner" /> {t('combat.loading')}
      </div>
    );
  }
  return <TrackerView db={db} tracker={readTracker(db.visible())} />;
}

function TrackerView({ db, tracker }: { db: ReplicaNodeDatabase; tracker: Tracker }) {
  const t = useT();
  const me = useApp((s) => s.user);
  const room = useApp((s) => s.room);
  const members = useApp((s) => s.members);
  const users = useApp((s) => s.users);
  const library = useApp((s) => s.library);
  const sendChat = useApp((s) => s.sendChat);
  const isGm = !!me && !!room && (room.ownerId === me.id || !!members[me.id]?.modes.includes('gm'));
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const rootId = db.visible().root.id;

  const write = useCallback(
    (ops: Op[]) => {
      if (!ops.length) return;
      try {
        db.write(ops);
      } catch (error) {
        toast(t('combat.writeFailed', { message: error instanceof Error ? error.message : String(error) }), 'error');
      }
    },
    [db, t],
  );

  const nextOrder = () => Math.max(0, ...tracker.actors.map((a) => a.order + 1));

  const addActor = async () => {
    const name = await askText({ title: t('combat.add'), label: t('combat.name'), initial: t('combat.actor.default'), maxLength: 60 });
    if (name) write(addActorOps(rootId, db.newId(), db.newId(), { name, side: 'foe' }, nextOrder()));
  };

  /** Quem é jogador na sala entra com o nome do personagem principal (ou o apelido). */
  const addPlayers = () => {
    let order = nextOrder();
    const ops: Op[] = [];
    const existing = new Set(tracker.actors.map((a) => a.name.toLowerCase()));
    for (const m of Object.values(members)) {
      if (!m.modes.includes('player')) continue;
      const character = m.mainCharacterId ? library.find((i) => i.id === m.mainCharacterId)?.name : undefined;
      const name = character ?? m.nick ?? users.find((u) => u.id === m.userId)?.displayName;
      if (!name || existing.has(name.toLowerCase())) continue;
      existing.add(name.toLowerCase());
      ops.push(...addActorOps(rootId, db.newId(), db.newId(), { name, side: 'friend' }, order++));
    }
    if (ops.length) write(ops);
    else toast(t('combat.noPlayers'), 'info');
  };

  const rollAll = async () => {
    const withRoll = tracker.actors.filter((a) => a.roll.trim());
    if (!withRoll.length) {
      toast(t('combat.noRolls'), 'info');
      return;
    }
    setBusy(true);
    const roomId = useApp.getState().activeRoomId;
    try {
      for (const actor of withRoll) {
        // trocou de mesa no meio: as rolagens que faltam iriam para o chat da mesa nova
        if (useApp.getState().activeRoomId !== roomId) return;
        try {
          const problem = rollProblem(actor.roll, t);
          if (problem) throw new Error(problem);
          // ator escondido (privado, ou de um token invisível): rola aqui, sem ir para o chat
          const total = actor.concealed ? rolar(actor.roll, localRng).total : (await useApp.getState().rollForPlugin(actor.roll, t('combat.rollLabel', { name: actor.name }))).total;
          if (useApp.getState().activeRoomId !== roomId) return;
          write(setOps(actor.id, { init: total }));
        } catch (error) {
          toast(t('combat.rollFailed', { name: actor.name, message: error instanceof Error ? error.message : String(error) }), 'error');
        }
      }
    } finally {
      setBusy(false);
    }
  };

  /** Grava a troca de vez (com `before`, as operações que vão antes dela) e anuncia no chat. */
  const announceTurn = (result: TurnChange, before: Op[] = [], back = false) => {
    write([...before, ...result.ops]);
    if (!result.actor) return;
    // texto simples: as cores clássicas do original (preto) somem no chat escuro
    // o chat é de todos: o ator escondido não aparece nele
    if (!result.actor.concealed) sendChat(back ? `◀ ${t('combat.turnBack', { name: result.actor.name })}` : `▶ ${t('combat.turnOf', { name: result.actor.name })}`);
    for (const { effect, actor } of result.ended) {
      if (actor.vis === 'public' && !actor.concealed) sendChat(`» ${t('combat.effectEnded', { effect: effect.name, name: actor.name })}`);
    }
  };

  const passTurn = () => {
    const result = nextTurn(tracker, rootId);
    if (result.actor) announceTurn(result);
  };

  const backTurn = () => {
    const result = prevTurn(tracker, rootId);
    if (result.actor) announceTurn(result, [], true);
  };

  /** A iniciativa nova; se quem está na vez muda de posição (adiou), a vez passa a quem vinha depois. */
  const setInit = (actor: Actor, init: number | '') => {
    const ops = setOps(actor.id, { init });
    const moved = sortActors(tracker.actors.map((a) => (a.id === actor.id ? { ...a, init: init === '' ? null : init } : a))).findIndex((a) => a.id === actor.id) !== tracker.actors.findIndex((a) => a.id === actor.id);
    const result = moved ? turnLeaves(tracker, rootId, actor.id, false) : null;
    if (result) announceTurn(result, ops);
    else write(ops);
  };

  const clear = async () => {
    const sure = await askConfirm({ title: t('combat.clear'), message: t('combat.clear.confirm'), action: t('combat.clear'), danger: true });
    if (sure) write([...tracker.actors.map((a): Op => ({ t: 'del', node: a.id })), ...resetOps(rootId)]);
  };

  const removeActor = async (actor: Actor) => {
    const sure = await askConfirm({ title: t('combat.remove'), message: t('combat.remove.confirm', { name: actor.name }), action: t('combat.remove'), danger: true });
    if (!sure) return;
    // a vez pode ter andado enquanto a pergunta estava aberta (outro mestre)
    const now = readTracker(db.visible());
    const result = turnLeaves(now, rootId, actor.id, true);
    const remove: Op[] = [{ t: 'del', node: actor.id }];
    // a vez sai antes: os efeitos de quem fica gastam a rodada, e o removido some depois
    if (result) announceTurn({ ...result, ops: [...result.ops, ...remove] });
    else write(remove);
  };

  const addEffect = async (actor: Actor) => {
    if (!actor.infoId) return;
    const name = await askText({ title: t('combat.effect.add'), label: t('combat.effect.name'), maxLength: 40 });
    if (!name) return;
    const rounds = await askText({ title: t('combat.effect.add'), label: t('combat.effect.rounds'), initial: '1', maxLength: 3 });
    const n = Math.trunc(Number(rounds));
    if (!rounds || !Number.isFinite(n) || n < 1) return;
    write(addEffectOps(actor.infoId, db.newId(), name, n));
  };

  return (
    <div className="rd-combat">
      <div className="rd-combat__bar">
        <span className="rd-combat__round" aria-live="polite">
          {t('combat.round')} <strong>{tracker.round}</strong>
        </span>
        <span className="rd-combat__grow" />
        {isGm ? (
          <>
            <button type="button" className="rd-combat__btn" onClick={backTurn} disabled={!tracker.actors.length} title={t('combat.prev')} aria-label={t('combat.prev')}>
              <Icon name="chevronLeft" size={14} />
            </button>
            <button type="button" className="rd-combat__btn rd-combat__btn--primary" onClick={passTurn} disabled={!tracker.actors.length}>
              {t('combat.next')}
            </button>
          </>
        ) : null}
      </div>
      {isGm ? (
        <div className="rd-combat__bar rd-combat__bar--tools">
          <>
            <button type="button" className="rd-combat__btn" onClick={() => void addActor()} title={t('combat.add')} aria-label={t('combat.add')}>
              <Icon name="plus" size={14} />
            </button>
            <button type="button" className="rd-combat__btn" onClick={addPlayers} title={t('combat.addPlayers')}>
              {t('combat.addPlayers.short')}
            </button>
            <button type="button" className="rd-combat__btn" onClick={() => void rollAll()} disabled={busy} title={t('combat.rollAll')}>
              {t('combat.rollAll.short')}
            </button>
            <button type="button" className="rd-combat__btn" onClick={() => void clear()} title={t('combat.clear')}>
              {t('combat.clear')}
            </button>
          </>
        </div>
      ) : null}
      {tracker.actors.length === 0 ? (
        <div className="rd-combat__empty">{isGm ? t('combat.empty.gm') : t('combat.empty')}</div>
      ) : (
        <ol className="rd-combat__list" aria-label={t('combat.title')}>
          {tracker.actors.map((actor) => {
            const current = tracker.turn === actor.id;
            const expanded = open === actor.id;
            return (
              <li key={actor.id} className={`rd-combat__actor rd-combat__actor--${actor.side || 'none'}${current ? ' is-current' : ''}`} aria-current={current ? 'step' : undefined} data-actor={actor.name}>
                <div className="rd-combat__row">
                  {isGm ? (
                    <input
                      className="rd-combat__init"
                      aria-label={t('combat.init', { name: actor.name })}
                      inputMode="decimal"
                      defaultValue={actor.init ?? ''}
                      key={`init-${actor.init ?? ''}`}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        const n = Number(v.replace(',', '.'));
                        if (v === '' ? actor.init !== null : Number.isFinite(n) && n !== actor.init) setInit(actor, v === '' ? '' : n);
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                    />
                  ) : (
                    <span className="rd-combat__init">{actor.init ?? '—'}</span>
                  )}
                  <button type="button" className="rd-combat__name" onClick={() => setOpen(expanded ? null : actor.id)} aria-expanded={expanded}>
                    {actor.name || t('combat.actor.default')}
                    {actor.invisible ? (
                      <span className="rd-combat__invisible" title={t('combat.invisible')} role="img" aria-label={t('combat.invisible')}>
                        <ConditionIcon id="invisible" size={14} />
                      </span>
                    ) : null}
                    {actor.effects.length ? <span className="rd-combat__fx">{actor.effects.length}</span> : null}
                  </button>
                  <button
                    type="button"
                    className="rd-combat__side"
                    title={t(`combat.side.${actor.side || 'none'}`)}
                    aria-label={t(`combat.side.${actor.side || 'none'}`)}
                    disabled={!isGm}
                    onClick={() => write(setOps(actor.id, { side: SIDE_NEXT[actor.side] }))}
                  />
                  {isGm ? (
                    <>
                      <button
                        type="button"
                        className={`rd-combat__vis rd-combat__vis--${actor.vis}`}
                        title={t(`combat.vis.${actor.vis}`)}
                        aria-label={t(`combat.vis.${actor.vis}`)}
                        onClick={() => actor.infoId && write(visibilityOps(actor.id, actor.infoId, VIS_NEXT[actor.vis], !!actor.sceneId))}
                      >
                        {actor.vis === 'public' ? '◉' : actor.vis === 'protected' ? '◐' : '○'}
                      </button>
                      <button type="button" className="rd-combat__del" title={t('combat.remove')} aria-label={t('combat.remove')} onClick={() => void removeActor(actor)}>
                        <Icon name="trash" size={13} />
                      </button>
                    </>
                  ) : null}
                </div>
                {expanded ? (
                  <div className="rd-combat__details">
                    {isGm ? (
                      <>
                        <label className="rd-combat__field">
                          <span>{t('combat.name')}</span>
                          <input defaultValue={actor.name} key={`n-${actor.name}`} maxLength={60} onBlur={(e) => e.target.value.trim() && e.target.value !== actor.name && write(setOps(actor.id, { name: e.target.value.trim() }))} />
                        </label>
                        <label className="rd-combat__field">
                          <span>{t('combat.rollExpr')}</span>
                          <input defaultValue={actor.roll} key={`r-${actor.roll}`} placeholder="1d20+2" maxLength={60} onBlur={(e) => e.target.value !== actor.roll && write(setOps(actor.id, { roll: e.target.value.trim() }))} />
                        </label>
                        {actor.sceneId ? (
                          <div className="rd-combat__link">
                            <span>{t('combat.link', { scene: library.find((i) => i.id === actor.sceneId)?.name ?? t('combat.link.gone') })}</span>
                            <button type="button" className="rd-combat__btn" onClick={() => write(linkActorOps(actor.id, null))}>
                              {t('combat.unlink')}
                            </button>
                          </div>
                        ) : null}
                      </>
                    ) : null}
                    {actor.infoId ? (
                      <>
                        {isGm ? (
                          <label className="rd-combat__field">
                            <span>{t('combat.notes')}</span>
                            <textarea defaultValue={actor.notes} key={`no-${actor.notes}`} rows={2} maxLength={1000} onBlur={(e) => e.target.value !== actor.notes && write(setOps(actor.infoId!, { notes: e.target.value }))} />
                          </label>
                        ) : actor.notes ? (
                          <p className="rd-combat__notes">{actor.notes}</p>
                        ) : null}
                        <div className="rd-combat__effects">
                          {actor.effects.map((fx) => (
                            <span key={fx.id} className="rd-combat__effect" title={t('combat.effect.left', { n: fx.rounds })}>
                              {fx.name} · {fx.rounds}
                              {isGm ? (
                                <button type="button" aria-label={t('combat.effect.remove', { name: fx.name })} onClick={() => write([{ t: 'del', node: fx.id }])}>
                                  <Icon name="close" size={10} />
                                </button>
                              ) : null}
                            </span>
                          ))}
                          {isGm ? (
                            <button type="button" className="rd-combat__btn" onClick={() => void addEffect(actor)}>
                              {t('combat.effect.add')}
                            </button>
                          ) : null}
                          {!isGm && !actor.effects.length && !actor.notes ? <span className="rd-combat__muted">{t('combat.nothing')}</span> : null}
                        </div>
                      </>
                    ) : (
                      <span className="rd-combat__muted">{t('combat.protected')}</span>
                    )}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
